/**
 * @file punycode.ts
 * @description Показ адреса магазина в национальном виде (`сайт.рф`) —
 * phase19.28 этап А (db-design-domains-a §1: «Показ `.рф` — при выводе, не
 * храним»). В базе адрес всегда в punycode (`xn--…`), человеку в кабинете и
 * в письмах показываем буквы.
 *
 * `url.domainToUnicode` есть только в Node, а функция нужна и кабинету в
 * браузере, поэтому декодер Punycode (RFC 3492, §6.2) написан здесь — без
 * зависимостей. Кодировать не нужно: ASCII-вид даёт WHATWG URL
 * (`normalizeHost`).
 */

const BASE = 36;
const T_MIN = 1;
const T_MAX = 26;
const SKEW = 38;
const DAMP = 700;
const INITIAL_BIAS = 72;
const INITIAL_N = 128;
const MAX_CODE_POINT = 0x10ffff;
const ACE_PREFIX = "xn--";

/** RFC 3492 §6.1: подстройка смещения после каждой вставленной буквы. */
function adapt(delta: number, numPoints: number, firstTime: boolean): number {
  let value = firstTime ? Math.floor(delta / DAMP) : Math.floor(delta / 2);
  value += Math.floor(value / numPoints);
  let k = 0;
  while (value > ((BASE - T_MIN) * T_MAX) >> 1) {
    value = Math.floor(value / (BASE - T_MIN));
    k += BASE;
  }
  return k + Math.floor(((BASE - T_MIN + 1) * value) / (value + SKEW));
}

/** Цифра основания 36: a–z → 0–25, 0–9 → 26–35; иное — BASE (ошибка). */
function digitOf(code: number): number {
  if (code >= 0x30 && code <= 0x39) return code - 22;
  if (code >= 0x41 && code <= 0x5a) return code - 0x41;
  if (code >= 0x61 && code <= 0x7a) return code - 0x61;
  return BASE;
}

/**
 * Раскодировать одну метку без префикса `xn--`; null — запись битая
 * (тогда показываем метку как есть, а не падаем: это только вывод).
 */
export function decodePunycodeLabel(input: string): string | null {
  const output: number[] = [];
  const basicEnd = Math.max(input.lastIndexOf("-"), 0);
  for (let j = 0; j < basicEnd; j += 1) {
    const code = input.charCodeAt(j);
    if (code >= 0x80) return null;
    output.push(code);
  }
  let n = INITIAL_N;
  let bias = INITIAL_BIAS;
  let i = 0;
  for (let index = basicEnd > 0 ? basicEnd + 1 : 0; index < input.length; ) {
    const oldI = i;
    let w = 1;
    for (let k = BASE; ; k += BASE) {
      if (index >= input.length) return null;
      const digit = digitOf(input.charCodeAt(index));
      index += 1;
      if (digit >= BASE) return null;
      i += digit * w;
      // Переполнение на честных адресах невозможно; число на краю — признак мусора.
      if (!Number.isSafeInteger(i)) return null;
      const t = k <= bias ? T_MIN : k >= bias + T_MAX ? T_MAX : k - bias;
      if (digit < t) break;
      w *= BASE - t;
    }
    const length = output.length + 1;
    bias = adapt(i - oldI, length, oldI === 0);
    n += Math.floor(i / length);
    i %= length;
    if (n > MAX_CODE_POINT) return null;
    output.splice(i, 0, n);
    i += 1;
  }
  return String.fromCodePoint(...output);
}

/** `xn--80aswg.xn--p1ai` → `сайт.рф`; ASCII-метки и битые `xn--` остаются как есть. */
export function hostToUnicode(host: string): string {
  return host
    .split(".")
    .map((label) => {
      if (!label.toLowerCase().startsWith(ACE_PREFIX)) return label;
      return decodePunycodeLabel(label.slice(ACE_PREFIX.length)) ?? label;
    })
    .join(".");
}
